import "./commercial-insight.css";

export type CommercialInsight = {
  temperature: "cold" | "warm" | "hot" | null;
  facts: string[];
  interpretation: string | null;
  evidence_message_ids: string[];
  suggested_next_action: string | null;
  summary: string;
  updated_at: string;
  latest_message_at?: string | null;
  source?: "human" | "automation" | null;
};

const labels = { cold: "Frio", warm: "Morno", hot: "Quente" } as const;
function TemperatureIcon({ temperature }: { temperature: Exclude<CommercialInsight["temperature"], null> }) {
  if (temperature === "cold") return <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M2.37 4.75l11.26 6.5M2.37 11.25l11.26-6.5M5.2 3.15 8 4.77l2.8-1.62M5.2 12.85 8 11.23l2.8 1.62" /></svg>;
  if (temperature === "warm") return <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="2.75" /><path d="M8 1.5v1.3M8 13.2v1.3M1.5 8h1.3M13.2 8h1.3M3.4 3.4l.92.92M11.68 11.68l.92.92M12.6 3.4l-.92.92M4.32 11.68l-.92.92" /></svg>;
  return <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8.52 1.55c.38 2.28-1.52 3.24-1.52 5.08 0 .92.55 1.54 1.29 1.54 1.03 0 1.65-.9 1.65-1.96 1.2.87 1.98 2.03 1.98 3.52 0 2.31-1.73 3.77-4.1 3.77-2.32 0-3.82-1.48-3.82-3.7 0-2.76 1.77-4.76 4.52-8.25Z" /></svg>;
}
const stale = (insight: CommercialInsight) =>
  Boolean(
    insight.latest_message_at &&
      new Date(insight.latest_message_at).getTime() >
        new Date(insight.updated_at).getTime(),
  );
const when = (value: string) =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

export function TemperaturePill({
  temperature,
  source,
}: {
  temperature: CommercialInsight["temperature"];
  source?: CommercialInsight["source"];
}) {
  if (!temperature) return null;
  return (
    <span
      className={`commercial-temperature ${temperature}`}
      title={`${labels[temperature]} · ${source === "human" ? "decisão humana" : "análise automática"}`}
    >
      <TemperatureIcon temperature={temperature} />
      <span>{labels[temperature]}</span>
      {source === "human" && <small>Humana</small>}
    </span>
  );
}

export function CommercialBrief({
  insight,
  loading = false,
  onRefresh,
  compact = false,
  expanded = true,
  onToggle,
}: {
  insight: CommercialInsight | null;
  loading?: boolean;
  onRefresh?: () => void;
  compact?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  // Other CRM surfaces keep their existing presentation. Only the Inbox
  // supplies onToggle and opts into the collapsible variant.
  if (!onToggle && loading)
    return (
      <section className="commercial-brief loading" aria-busy="true">
        <span className="commercial-brief-kicker">RESUMO COMERCIAL DA IA</span>
        <p>Carregando o último resumo disponível…</p>
      </section>
    );
  if (!onToggle && !insight)
    return (
      <section className="commercial-brief empty">
        <span className="commercial-brief-kicker">RESUMO COMERCIAL DA IA</span>
        <p>
          Ainda não há um resumo para esta conversa. O atendimento e a tomada
          humana continuam disponíveis normalmente.
        </p>
      </section>
    );
  const isStale = insight ? stale(insight) : false;
  return (
    <section
      className={`commercial-brief ${compact ? "compact" : ""} ${expanded ? "" : "collapsed"} ${loading ? "loading" : ""} ${!insight ? "empty" : ""}`}
      aria-label="Resumo comercial interno"
      aria-busy={loading}
    >
      <header>
        <div>
          <span className="commercial-brief-kicker">
            RESUMO COMERCIAL DA IA
          </span>
          {expanded && insight && <strong>Uso interno da equipe</strong>}
        </div>
        {onToggle ? (
          <div className="commercial-brief-actions">
            {insight && (
              <TemperaturePill
                temperature={insight.temperature}
                source={insight.source}
              />
            )}
            <button
              type="button"
              className="commercial-brief-toggle"
              aria-expanded={expanded}
              onClick={onToggle}
            >
              {expanded ? "Recolher" : "Expandir"}
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="m4 6 4 4 4-4" />
              </svg>
            </button>
          </div>
        ) : insight ? (
            <TemperaturePill
              temperature={insight.temperature}
              source={insight.source}
            />
        ) : null}
      </header>
      {!expanded ? (
        <div className="commercial-brief-glance">
          <p>
            {loading
              ? "Carregando o último resumo disponível…"
              : insight?.summary || "Ainda não há um resumo para esta conversa."}
          </p>
          {isStale && <span title="Há mensagens mais recentes">Desatualizado</span>}
        </div>
      ) : loading ? (
        <p>Carregando o último resumo disponível…</p>
      ) : !insight ? (
        <p>
          Ainda não há um resumo para esta conversa. O atendimento e a tomada
          humana continuam disponíveis normalmente.
        </p>
      ) : (
        <>
          {isStale && (
            <div className="commercial-stale">
              <span>Há mensagens mais recentes. Este é o último resumo salvo.</span>
              {onRefresh && (
                <button type="button" onClick={onRefresh}>
                  Atualizar visualização
                </button>
              )}
            </div>
          )}
          {insight.summary && (
            <p className="commercial-summary">{insight.summary}</p>
          )}
          <div className="commercial-brief-grid">
            <div>
              <span>FATOS CONFIRMADOS</span>
              {insight.facts.length ? (
                <ul>
                  {insight.facts.slice(0, 4).map((fact, index) => (
                    <li key={`${fact}-${index}`}>{fact}</li>
                  ))}
                </ul>
              ) : (
                <p>Sem fatos confirmados ainda.</p>
              )}
            </div>
            <div>
              <span>LEITURA COMERCIAL</span>
              <p>
                {insight.interpretation ||
                  "Ainda não há interpretação comercial confiável."}
              </p>
            </div>
            {insight.suggested_next_action && (
              <div className="commercial-next-action">
                <span>PRÓXIMA AÇÃO SUGERIDA</span>
                <p>{insight.suggested_next_action}</p>
              </div>
            )}
          </div>
          <footer>
            <span>
              Atualizado em {when(insight.updated_at)} ·{" "}
              {insight.source === "human" ? "decisão humana" : "análise automática"}
            </span>
            {insight.evidence_message_ids.length > 0 && (
              <span>
                {insight.evidence_message_ids.length} evidência
                {insight.evidence_message_ids.length === 1 ? "" : "s"} na conversa
              </span>
            )}
          </footer>
        </>
      )}
    </section>
  );
}
